import { Link } from "react-router";
import Picture from "../Picture/Picture.jsx";
import styles from "./TestimonialGrid.module.css";
import { LOGO_VERSION, withVersion } from "../../lib/assetVersions.js";

const AVATAR = { ratio: "1 / 1", widths: [160, 320], fallbackWidth: 320 };

/** Five marks in signal orange — punctuation for the quote, not a score anyone gave. */
function Stars({ className = "" }) {
  return (
    <span className={`${styles.stars} ${className}`.trim()} aria-hidden="true">
      {[0, 1, 2, 3, 4].map((i) => (
        <svg key={i} viewBox="0 0 20 19" className={styles.star} focusable="false">
          <path d="M10 0l2.6 6.3 6.8.5-5.2 4.4 1.6 6.6L10 14.3 4.2 17.8l1.6-6.6L.6 6.8l6.8-.5z" />
        </svg>
      ))}
    </span>
  );
}

function Avatar({ item, size }) {
  const className = `${styles.avatar} ${size === "lg" ? styles.avatarLg : ""}`.trim();
  if (item.photo) {
    return (
      <Picture
        src={item.photo}
        alt={item.imageAlt || item.author}
        {...AVATAR}
        sizes={size === "lg" ? "160px" : "80px"}
        className={className}
      />
    );
  }
  if (item.logo) {
    return (
      <span className={`${className} ${styles.avatarLogo}`}>
        <img
          src={withVersion(`${item.logo}.png`, LOGO_VERSION)}
          alt={item.imageAlt || item.org}
          loading="lazy"
          width="96"
          height="96"
        />
      </span>
    );
  }
  return null;
}

function Who({ item }) {
  return (
    <div className={styles.whoText}>
      <p className={styles.name}>{item.author}</p>
      {item.role ? <p className={styles.role}>{item.role}</p> : null}
      <p className={styles.org}>{item.org}</p>
      {item.project ? <p className={styles.project}>{item.project}</p> : null}
    </div>
  );
}

function Links({ item }) {
  if (!item.link && !item.video) return null;
  return (
    <p className={styles.links}>
      {item.link ? (
        <Link to={item.link.to} className={styles.link}>
          {item.link.label}
        </Link>
      ) : null}
      {item.video ? (
        <a
          href={item.video.href}
          className={styles.link}
          target="_blank"
          rel="noreferrer"
          data-video="true"
        >
          {item.video.label}
        </a>
      ) : null}
    </p>
  );
}

/**
 * One card. It is an <article> so it can sit inside a grid's <li> or a carousel's, and
 * `clamped` caps the quote's height for rows that have to share one.
 */
export function TestimonialCard({ item, clamped = false }) {
  return (
    <article className={`${styles.card} ${clamped ? styles.clamped : ""}`.trim()}>
      <Stars />
      <blockquote className={styles.quote}>
        <p>&ldquo;{item.quote}&rdquo;</p>
      </blockquote>
      <footer className={styles.who}>
        <Avatar item={item} size="sm" />
        <Who item={item} />
      </footer>
      <Links item={item} />
    </article>
  );
}

/**
 * One quote as a row of a list rather than a card: who is speaking on the left, the quote
 * in larger type beside them. For a group read top to bottom, like a transcript.
 */
export function TestimonialRow({ item }) {
  return (
    <article className={styles.row}>
      <footer className={styles.rowWho}>
        <Avatar item={item} size="sm" />
        <Who item={item} />
      </footer>
      <div className={styles.rowBody}>
        <blockquote className={styles.rowQuote}>
          <p>&ldquo;{item.quote}&rdquo;</p>
        </blockquote>
        <Links item={item} />
      </div>
    </article>
  );
}

/**
 * The quote's number as the headline, for a testimonial that states one (`stat`). The full
 * quote stays under it, so the figure is never read without its source.
 */
export function StatCard({ item }) {
  return (
    <article className={`${styles.card} ${styles.statCard}`}>
      <p className={styles.stat}>
        <span className={styles.statValue}>{item.stat.value}</span>
        <span className={styles.statLabel}>{item.stat.label}</span>
      </p>
      <blockquote className={styles.statQuote}>
        <p>&ldquo;{item.quote}&rdquo;</p>
      </blockquote>
      <footer className={styles.who}>
        <Avatar item={item} size="sm" />
        <Who item={item} />
      </footer>
    </article>
  );
}

/** A wide card for the one quote a row should lead with (`spotlight`): bigger type and face. */
export function SpotlightCard({ item }) {
  return (
    <article className={`${styles.card} ${styles.spotlight}`}>
      <div className={styles.spotlightBody}>
        <Stars />
        <blockquote className={styles.spotlightQuote}>
          <p>&ldquo;{item.quote}&rdquo;</p>
        </blockquote>
        <footer className={styles.spotlightWho}>
          <Who item={item} />
        </footer>
        <Links item={item} />
      </div>
      <div className={styles.spotlightFigure}>
        <Avatar item={item} size="lg" />
      </div>
    </article>
  );
}

/**
 * Every item as a card, three across. `stats` leads a quote that states a number (`stat`)
 * with that number, as a StatCard.
 */
export default function TestimonialGrid({ items, stats = false }) {
  if (!items.length) return null;
  return (
    <ul className={`grid grid-3 ${styles.grid}`}>
      {items.map((item) => (
        <li key={item.quote} className={styles.cell}>
          {stats && item.stat ? <StatCard item={item} /> : <TestimonialCard item={item} />}
        </li>
      ))}
    </ul>
  );
}
